import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { isAdmin } from '../utils/role';

const CATEGORIES = ['Project', 'Product', 'Report', 'AMS', 'Demo', 'Testing', 'Others'];
const STATUSES = ['PLANNING', 'ACTIVE', 'ON_HOLD', 'COMPLETED', 'CANCELLED'];
const emptyForm = { projectName: '', projectCode: '', companyId: '', description: '', startDate: '', endDate: '', status: 'PLANNING', priority: 'MEDIUM', category: 'Project', liveUrl: '' };

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [companyFilter, setCompanyFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [adminEnabled, setAdminEnabled] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [p, c, cfg] = await Promise.all([
        api.get('/projects'),
        api.get('/companies'),
        api.get('/projects/config').catch(() => ({ data: {} })),
      ]);
      setProjects(p.data);
      setCompanies(c.data);
      setAdminEnabled(!!cfg.data.projectAdminEnabled);
    } catch (e) {
      setError(e.response?.status
        ? `Failed to load projects (HTTP ${e.response.status}${e.response.data?.error ? ': ' + e.response.data.error : ''})`
        : 'Failed to load projects — is the backend running on :9093?');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const canManage = adminEnabled && isAdmin();
  const companyName = (id) => companies.find(c => c.id === id)?.name || '—';
  const shown = companyFilter
    ? projects.filter(p => String(p.companyId) === String(companyFilter))
    : projects;

  const openNew = () => { setForm(emptyForm); setEditing(null); setFormError(''); setShowForm(true); };

  // Fetches the next auto code for the chosen company (new projects only).
  const fetchNextCode = async (companyId) => {
    if (!companyId) { setForm(f => ({ ...f, projectCode: '' })); return; }
    try {
      const { data } = await api.get('/projects/next-code', { params: { companyId } });
      setForm(f => ({ ...f, projectCode: data.code || '' }));
    } catch { setForm(f => ({ ...f, projectCode: '' })); }
  };

  const onCompanyChange = (companyId) => {
    setForm(f => ({ ...f, companyId }));
    if (!editing) fetchNextCode(companyId);
  };
  const openEdit = (p) => {
    setForm({
      projectName: p.projectName || '', projectCode: p.projectCode || '', companyId: p.companyId || '',
      description: p.description || '', startDate: p.startDate || '', endDate: p.endDate || '',
      status: p.status || 'PLANNING', priority: p.priority || 'MEDIUM', category: p.category || 'Project', liveUrl: p.liveUrl || '',
    });
    setEditing(p.id); setFormError(''); setShowForm(true);
  };

  const save = async () => {
    setFormError('');
    if (!form.projectName) { setFormError('Project name is required'); return; }
    if (!editing && !form.companyId) { setFormError('Select a company — the project code is generated from it'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        companyId: form.companyId ? Number(form.companyId) : null,
        startDate: form.startDate || null,
        endDate: form.endDate || null,
      };
      if (editing) await api.put(`/projects/${editing}`, payload);
      else await api.post('/projects', payload);
      setShowForm(false); setEditing(null); setForm(emptyForm); load();
    } catch (e) {
      setFormError(e.response?.data?.error || 'Failed to save project');
    } finally { setSaving(false); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: '#0f172a' }}>Projects</h1>
          <p style={{ color: '#64748b' }}>{canManage ? 'Projects & products (shared CLTS directory)' : 'Projects from the shared CLTS directory (read-only)'}</p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <div style={{ width: 220 }}>
            <select value={companyFilter} onChange={e => setCompanyFilter(e.target.value)}>
              <option value="">All companies</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          {canManage && <button className="btn-primary" onClick={openNew}>+ New Project</button>}
        </div>
      </div>

      {error && <div style={{ background: '#fee2e2', color: '#b91c1c', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13 }}>{error}</div>}

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Loading...</div>
        ) : shown.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>{error ? 'Could not load projects.' : 'No projects found.'}</div>
        ) : (
          <table>
            <thead>
              <tr><th>Code</th><th>Name</th><th>Company</th><th>Category</th><th>Status</th>{canManage && <th></th>}</tr>
            </thead>
            <tbody>
              {shown.map(p => (
                <tr key={p.id}>
                  <td style={{ fontWeight: 600 }}>{p.projectCode || '—'}</td>
                  <td>{p.projectName}</td>
                  <td>{companyName(p.companyId)}</td>
                  <td>{p.category || '—'}</td>
                  <td>{p.status || '—'}</td>
                  {canManage && <td><button className="btn-ghost" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => openEdit(p)}>Edit</button></td>}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 }} onClick={() => setShowForm(false)}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 28, width: 540, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontWeight: 700, marginBottom: 18, color: '#0f172a', fontSize: 18 }}>{editing ? 'Edit Project' : 'New Project'}</h3>
            {formError && <div style={{ background: '#fee2e2', color: '#b91c1c', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13 }}>{formError}</div>}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px' }}>
              <Field label="Project Name *"><input value={form.projectName} onChange={e => setForm({ ...form, projectName: e.target.value })} /></Field>
              <Field label="Company *">
                <select value={form.companyId} onChange={e => onCompanyChange(e.target.value)}>
                  <option value="">— Select —</option>
                  {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </Field>
              <Field label="Project Code (auto)">
                <input value={form.projectCode} disabled readOnly style={{ opacity: 0.7, background: 'var(--gray-100)' }}
                  placeholder={editing ? '' : 'Select a company to generate'} />
              </Field>
              <Field label="Category">
                <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Status">
                <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                  {STATUSES.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                </select>
              </Field>
              <Field label="Start Date"><input type="date" value={form.startDate} onChange={e => setForm({ ...form, startDate: e.target.value })} /></Field>
              <Field label="End Date"><input type="date" value={form.endDate} onChange={e => setForm({ ...form, endDate: e.target.value })} /></Field>
              <div style={{ gridColumn: '1 / -1' }}>
                <Field label="Live URL"><input value={form.liveUrl} onChange={e => setForm({ ...form, liveUrl: e.target.value })} placeholder="https://..." /></Field>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <Field label="Description"><textarea rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></Field>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button className="btn-primary" onClick={save} disabled={saving} style={{ flex: 1 }}>{saving ? 'Saving...' : (editing ? 'Update' : 'Create')}</button>
              <button className="btn-ghost" onClick={() => setShowForm(false)} style={{ flex: 1 }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4, color: '#374151' }}>{label}</label>
      {children}
    </div>
  );
}
